'use client';

import { VariantProps, cva } from 'class-variance-authority';
import { JSXElementConstructor, ReactNode, forwardRef } from 'react';

import { cn } from '@/lib/css-utils';

import { BaseUIComponentProps, defaultBaseUIComponentProps } from './base';
import { useGlobalDefaults } from './provider';

export type ParagraphFontWeight = 'regular' | 'medium' | 'light';
export type ParagraphSize = 'xxs' | 'xs' | 'sm' | 'md';

const paragraphVariants = cva('font-roboto', {
    variants: {
        variant: {
            title: '',
            subtitle: '',
        },

        mode: {
            dark: '',
            light: '',
        },

        fontWeight: {
            regular: 'font-normal',
            medium: 'font-medium',
            light: 'font-light',
        },

        size: {
            md: 'text-lg',
            sm: 'text-md',
            xs: 'text-sm',
            xxs: 'text-xs',
        },
    },

    compoundVariants: [
        {
            mode: 'dark',
            variant: 'title',
            className: 'text-token-text-light-default',
        },
        {
            mode: 'light',
            variant: 'title',
            className: 'text-black',
        },

        {
            mode: 'dark',
            variant: 'subtitle',
            className: 'text-token-text-subtitle-default',
        },
        {
            mode: 'light',
            variant: 'subtitle',
            className: 'text-black',
        },
    ],

    defaultVariants: {
        mode: defaultBaseUIComponentProps.mode,
        fontWeight: 'regular',
        size: 'xs',
        variant: 'title',
    },
});

export type ParagraphVariants = VariantProps<typeof paragraphVariants>['variant'];

export interface ParagraphProps extends BaseUIComponentProps {
    children: ReactNode;

    variant?: ParagraphVariants;

    fontWeight?: ParagraphFontWeight;
    size?: ParagraphSize;

    as?: keyof React.JSX.IntrinsicElements | JSXElementConstructor<any>;

    title?: string;
}

/**
 * @deprecated
 */
export const Paragraph = forwardRef(
    (
        {
            children,
            mode,
            fontWeight = 'light',
            size = 'xs',
            variant = 'title',
            as = undefined,
            className,
            title,
        }: ParagraphProps,
        ref
    ) => {
        const Component = typeof as !== 'undefined' ? as : 'p';

        const defaults = useGlobalDefaults(defaultBaseUIComponentProps);

        return (
            <Component
                ref={ref}
                className={cn(
                    paragraphVariants({
                        mode: mode ?? defaults.mode,
                        fontWeight,
                        size,
                        variant,
                    }),
                    className
                )}
                title={title}
            >
                {children}
            </Component>
        );
    }
);

Paragraph.displayName = 'Paragraph';

export const SubTitle = forwardRef(({ children, ...props }: ParagraphProps, ref) => {
    return (
        <Paragraph variant="subtitle" {...props} ref={ref}>
            {children}
        </Paragraph>
    );
});

SubTitle.displayName = 'SubTitle';
